import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import apiClient from '../api/client'
import { registrarRevision } from '../api/revision'
import { useAvisoMensajes } from '../useAvisoMensajes'
import { errorCode, errorMessage, isApiError } from '../api/ApiError'
import ConversationList from '../components/inbox/ConversationList'
import ChatThread, { type QuickTemplate } from '../components/inbox/ChatThread'
import SelectorAsesor from '../components/inbox/SelectorAsesor'
import type { ContextTab } from '../components/inbox/ContextPanel'
import PanelDelCaso from '../components/inbox/PanelDelCaso'
import EmptyState from '../components/EmptyState'
import PreguntarAlSistema from '../components/PreguntarAlSistema'
import { useAuth } from '../auth/useAuth'
import { useMe } from '../auth/useMe'
import AvisoSinConexion from '../components/inbox/AvisoSinConexion'
import type {
  Advisor, ConversationListResponse, ConversationSummary, Message, SesionSocio,
} from '../types/portal'
import { telefonoVisible } from '../identidad'
import { BANDEJA_CAMBIO, MENSAJE_NUEVO } from '../eventos'
import { completoPorIA } from '../atencion'
import {
  alLlegarLaLista, alResolver, alSoltar, avisosIniciales, respuestaVigente, textoDeLaNueva,
  type AvisosDeLaBandeja, type Salida,
} from '../bandejaAvisos'
import { esDefinitivo } from '../hilo'
import { delServidor, ordenDeLaLista, type VistosDelServidor } from '../noLeidos'
import { useCopiloto } from '../useCopiloto'
import { SESION_DEVUELTA, horaEnLima, sinCanalConElCliente, sinPlantillas } from '../sesionSocio'
import { useHilo } from '../useHilo'
import {
  BORRADOR_PEDIR_DE_NUEVO, avisoDelPedido, borradorSinElPedido, cuerpoDeRespuesta,
  etiquetaDelPedido, pedidoDeNuevo, type PedidoDeNuevo,
} from '../pedirDeNuevo'

/**
 * "Mi bandeja" — el espacio de trabajo del asesor y la razón del rediseño.
 *
 * Tres columnas: conversaciones asignadas · chat de WhatsApp · panel con
 * Copiloto IA, documentos y ficha. El asesor atiende de punta a punta sin
 * cambiar de módulo; antes tenía que ir a Conversaciones, volver a Documentos
 * para revisar un DNI, y perder el hilo.
 *
 * El alcance lo recorta el SERVIDOR: `/conversations` con rol Asesor solo
 * devuelve las asignadas a quien pregunta, y el detalle responde 403 si la
 * conversación es de otro. Acá no hay filtro de seguridad, solo presentación.
 */

// Cada cuánto se refresca la LISTA por su cuenta. Es la red, no el camino: lo
// que hace aparecer una asignación al instante es `BANDEJA_CAMBIO`, que emite
// el contador del sidebar cada vez que cambia lo asignado a quien mira. Este
// sondeo cubre lo que el contador no ve —mira las 100 conversaciones más
// nuevas; la lista, hasta mil—.
const POLL_MS = 15_000

// El HILO ABIERTO se refresca más seguido, y de a un pedido por vez: ver
// `hilo.ts` (el ritmo) y `useHilo.ts` (el cableado, el mismo de En vivo).

interface KnowledgeTemplate {
  id: string
  title: string
  content: string
  type: string
}

export default function Inbox() {
  const { canSupervise, atiende } = useAuth()
  const { refresh: refreshMe } = useMe()

  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  // Las que resolvió quien mira y siguen cerradas (DEF-047). Van APARTE de las
  // asignadas: el aviso de «nueva asignada» y la que «se fue» miran solo esas,
  // y una resuelta que el cliente reabre pasa de esta lista a la otra.
  const [resueltas, setResueltas] = useState<ConversationSummary[]>([])
  // Lo que quien mira ya vio, como llegó con la lista: el número verde.
  const [vistosServidor, setVistosServidor] = useState<VistosDelServidor | null>(null)
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)

  const [selectedId, setSelectedId] = useState<string | null>(null)

  const [suggestionOpen, setSuggestionOpen] = useState(true)
  const [tab, setTab] = useState<ContextTab>('copiloto')

  const [draft, setDraft] = useState('')
  // DEF-043: el borrador es un «Pedir de nuevo» de ESTE documento. Con el chip
  // puesto el mensaje sale sin tomar la conversación; se va con el borrador.
  // DEF-066: el requisito cambia cuando ese mensaje SALE, no al poner el chip.
  const [pedido, setPedido] = useState<PedidoDeNuevo | null>(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [resolving, setResolving] = useState(false)
  const [devolviendo, setDevolviendo] = useState(false)
  const [extendiendo, setExtendiendo] = useState(false)
  // Aviso flotante de una acción que salió bien. Se va solo: confirma, no pide
  // nada, y un banner fijo competiría con el de «nueva asignada».
  const [aviso, setAviso] = useState<string | null>(null)
  // La que el asesor devolvió al agente. Si después sale de la bandeja, el
  // banner tiene que decir que se fue con el agente, no «la tomó otro».
  const devueltaAlAgente = useRef<string | null>(null)
  const [reactivating, setReactivating] = useState(false)
  const [reviewingDoc, setReviewingDoc] = useState<string | null>(null)

  const [templates, setTemplates] = useState<QuickTemplate[]>([])

  // Las dos bandas: la que llegó (se detecta comparando los ids entre dos
  // respuestas: el backend no empuja eventos) y la que se fue —se la
  // reasignaron a otro, la intervino un supervisor o la liberaron; sin esto
  // el chat se quedaba en «Esta conversación ya no está asignada a ti» para
  // siempre—. Las reglas viven en `bandejaAvisos.ts` (DEF-067): una banda por
  // conversación, manda la más reciente, e irse se confirma. El ref es lo que
  // leen las respuestas que llegan; el estado, lo que se pinta.
  const avisos = useRef<AvisosDeLaBandeja<ConversationSummary>>(avisosIniciales())
  const [bandas, setBandas] = useState<{
    nueva: ConversationSummary | null; salio: Salida<ConversationSummary> | null
  }>({ nueva: null, salio: null })
  const { nueva, salio } = bandas
  const aplicarAvisos = useCallback((siguientes: AvisosDeLaBandeja<ConversationSummary>) => {
    avisos.current = siguientes
    setBandas({ nueva: siguientes.nueva, salio: siguientes.salio })
  }, [])
  // Cada pedido de la lista lleva número (DEF-067): hay cinco disparadores que
  // se solapan, y una respuesta vieja que llega tarde no puede pisar a una
  // más nueva ni a lo que quien mira acaba de hacer (soltar, resolver).
  const pedidosLista = useRef(0)
  const ultimaAplicada = useRef(0)
  // La abierta a la que el hilo le recibió un 403/404: ya no es de quien mira,
  // y la lista no tiene que esperar una segunda respuesta para soltarla.
  const fueraConfirmada = useRef<string | null>(null)
  // Reasignar desde la bandeja: supervisión y Admin (ver `SelectorAsesor`).
  const [reasignando, setReasignando] = useState(false)
  const [advisors, setAdvisors] = useState<Advisor[]>([])
  const [reasignandoBusy, setReasignandoBusy] = useState(false)
  // La lista las lee con los valores del momento: `fetchList` no se recrea en
  // cada selección, porque de ella cuelgan el sondeo y los eventos.
  const seleccionada = useRef<string | null>(null)

  // ── Listado ────────────────────────────────────────────────────────────────

  const fetchList = useCallback(async (silencioso = false) => {
    if (!silencioso) setListLoading(true)
    const numero = ++pedidosLista.current
    try {
      // `resueltas=1`: en el MISMO pedido, las que resolví y siguen cerradas
      // (DEF-047). Un pedido más cada 15 s era el ritmo que ya trababa el hilo.
      const res = await apiClient.get<ConversationListResponse>(
        '/conversations?scope=mine&limit=100&resueltas=1',
      )
      // Llegó después de una más nueva o de algo que hizo quien mira: no vale.
      if (!respuestaVigente(numero, ultimaAplicada.current)) return
      ultimaAplicada.current = numero
      const items = res.data.conversations ?? []
      const cerradas = res.data.resueltas ?? []
      setConversations(items)
      setResueltas(cerradas)
      setVistosServidor(delServidor(res.data))
      setListError(null)

      // Qué banda se prende o se apaga, y si la abierta se fue de verdad.
      const tras = alLlegarLaLista(avisos.current, {
        asignadas: items,
        resueltas: cerradas,
        abierta: seleccionada.current,
        fueraConfirmada: fueraConfirmada.current,
        devueltaAlAgente: devueltaAlAgente.current,
      })
      aplicarAvisos(tras.avisos)
      // La abierta vino en la lista: un 403 de antes ya no confirma nada.
      if (tras.avisos.faltas === 0) fueraConfirmada.current = null
      if (tras.soltarLaAbierta) {
        // Sin selección, el efecto de abajo abre la primera que quede. El
        // borrador se va con ella: era para ESE cliente, y quedaría cargado en
        // el compositor del siguiente —el mismo motivo del reset de
        // `seleccionar`, que la apertura automática no pasa—. El hilo se vacía
        // solo al quedar sin selección (`useHilo`).
        fueraConfirmada.current = null
        seleccionada.current = null
        setSelectedId(null)
        setDraft('')
        setPedido(null)
        setSendError(null)
        setTab('copiloto')
        setSuggestionOpen(true)
      }
    } catch (err) {
      if (respuestaVigente(numero, ultimaAplicada.current)) {
        setListError(errorMessage(err, 'No se pudo cargar tu bandeja.'))
      }
    } finally {
      if (!silencioso) setListLoading(false)
    }
  }, [aplicarAvisos])

  useEffect(() => { void fetchList() }, [fetchList])

  useEffect(() => {
    const id = setInterval(() => { void fetchList(true) }, POLL_MS)
    return () => clearInterval(id)
  }, [fetchList])

  // ── Hilo y copiloto ───────────────────────────────────────────────────────

  // El hilo sondea cada 2,5 s y se entera antes que nadie de que la
  // conversación ya no es de quien mira: la lista no espera a su turno para
  // soltarla. Mientras el asesor envía no sondea, para no pisar la burbuja
  // optimista con una respuesta que todavía no la incluye.
  const hilo = useHilo(selectedId, {
    pausado: sending,
    mensajeDeError: (err) => (errorCode(err) === 'no_asignada'
      ? 'Esta conversación ya no está asignada a ti.'
      : errorMessage(err, 'No se pudo cargar la conversación.')),
    // 403/404: la conversación ya no es de quien mira. Es la confirmación que
    // la lista necesita para soltarla al primer faltazo (DEF-067).
    alFallar: (err) => {
      if (errorCode(err) === 'no_asignada'
          || (isApiError(err) && esDefinitivo(err.status, err.code))) {
        fueraConfirmada.current = seleccionada.current
        void fetchList(true)
      }
    },
  })
  const { detail, refrescar: refrescarHilo, reintentar: reintentarHilo, editar: editarHilo } = hilo
  // Al día con el hilo, no solo al abrirlo (DEF-029): ver `useCopiloto`.
  const copiloto = useCopiloto(selectedId, detail, tab === 'copiloto')

  // Al instante: cuando el contador ve que cambió lo asignado, y al volver a
  // la pestaña —con la pestaña de fondo el navegador espacia los timers—.
  useEffect(() => {
    const refrescar = () => { void fetchList(true) }
    const alVolver = () => { if (document.visibilityState === 'visible') refrescar() }
    window.addEventListener(BANDEJA_CAMBIO, refrescar)
    // Un cliente escribió: la fila sube con su número verde junto con el
    // sonido, no en el próximo turno de la lista.
    window.addEventListener(MENSAJE_NUEVO, refrescar)
    document.addEventListener('visibilitychange', alVolver)
    return () => {
      window.removeEventListener(BANDEJA_CAMBIO, refrescar)
      window.removeEventListener(MENSAJE_NUEVO, refrescar)
      document.removeEventListener('visibilitychange', alVolver)
    }
  }, [fetchList])

  useEffect(() => { seleccionada.current = selectedId }, [selectedId])

  // Plantillas rápidas del compositor.
  useEffect(() => {
    apiClient
      .get<{ knowledge: KnowledgeTemplate[] }>('/knowledge?type=TEMPLATE&limit=20')
      .then((res) => {
        setTemplates(
          (res.data.knowledge ?? [])
            .filter((t) => t.content)
            .map((t) => ({ key: t.id, label: t.title, body: t.content })),
        )
      })
      .catch(() => {
        // Sin plantillas el compositor sigue funcionando: son un atajo, no un
        // requisito para responder.
      })
  }, [])

  // ── Abrir la que se pidió ─────────────────────────────────────────────────

  // `?c=<id>` la trae «Intervenir» desde En vivo: la conversación que acabas de
  // tomar es la que hay que abrir, no la primera de la lista. Si todavía no
  // llegó —el índice tarda un instante en reflejar la asignación— se vuelve a
  // pedir la lista una vez; el parámetro se consume al abrirla, para que no
  // mande sobre la selección de ahí en más.
  const [params, setParams] = useSearchParams()
  const pedida = params.get('c')
  const reintentoPedida = useRef(false)
  useEffect(() => {
    if (!pedida || listLoading) return
    if (conversations.some((c) => c.conversation_id === pedida)
        || resueltas.some((c) => c.conversation_id === pedida)) {
      seleccionada.current = pedida
      setSelectedId(pedida)
      setParams({}, { replace: true })
    } else if (!reintentoPedida.current) {
      reintentoPedida.current = true
      const t = setTimeout(() => { void fetchList(true) }, 1500)
      return () => clearTimeout(t)
    } else {
      // Tampoco llegó en el reintento: se suelta el pedido y la bandeja abre
      // la primera, como siempre. Quedarse esperando la dejaría sin nada.
      setParams({}, { replace: true })
    }
  }, [pedida, conversations, resueltas, listLoading, fetchList, setParams])

  // ── Selección automática ──────────────────────────────────────────────────

  // Al entrar (o al resolverse la abierta) se selecciona la primera. El asesor
  // llega a la bandeja para atender, no para elegir de una lista vacía.
  //
  // La primera EN CURSO: abrir de entrada una completa por el agente —que la
  // lista muestra aparte— dejaría el chat abierto sobre una fila que no se ve.
  // Y la primera en el orden de la lista: una que requiere revisión por una
  // alerta crítica va arriba (DEF-039), y después quien escribió último.
  useEffect(() => {
    if (selectedId || conversations.length === 0 || pedida) return
    const enCurso = ordenDeLaLista(conversations)
      .find((c) => (c.activity?.is_live ?? true) && !completoPorIA(c))
    setSelectedId((enCurso ?? conversations[0]).conversation_id)
  }, [conversations, selectedId, pedida])

  const seleccionar = useCallback((id: string) => {
    setSelectedId(id)
    // Reset explícito: la pestaña vuelve a Copiloto, el borrador se limpia y la
    // sugerencia se muestra de nuevo. Sin esto, el borrador escrito para un
    // cliente queda cargado al abrir la conversación del siguiente. El
    // Copiloto se olvida solo al cambiar de conversación (`useCopiloto`).
    setTab('copiloto')
    setDraft('')
    setPedido(null)
    setSuggestionOpen(true)
    setSendError(null)
  }, [])

  // El id del último mensaje del cliente que llegó con el hilo abierto, para
  // que su burbuja entre con animación. El sonido lo hace el Layout.
  const recienLlegado = useAvisoMensajes(selectedId, detail?.messages)

  // ── Acciones ──────────────────────────────────────────────────────────────

  const enviar = useCallback(async () => {
    const texto = draft.trim()
    if (!texto || !detail || sending) return
    setSending(true)
    setSendError(null)

    // Burbuja optimista: el asesor ve su mensaje al instante y se quita si el
    // envío falla.
    const optimista: Message = {
      message_id: `optimista-${Date.now()}`,
      sender: 'operator',
      text: texto,
      timestamp: new Date().toISOString(),
      delivery_status: '', failure_code: '', failure_title: '', failure_msg: '',
    }
    editarHilo((prev) => prev ? { ...prev, messages: [...prev.messages, optimista] } : prev)
    setDraft('')
    setSuggestionOpen(false)

    try {
      // Con el chip de «Pedir de nuevo» viaja el documento y el agente sigue
      // conduciendo (DEF-043); sin él, escribir toma la conversación.
      await apiClient.post('/conversations/reply',
                           cuerpoDeRespuesta(detail.conversation_id, texto, pedido))
      setPedido(null)
      // Refetch corto: la Lambda de conversación persiste el mensaje y el
      // meta_msg_id después de que Meta acepta el envío.
      setTimeout(refrescarHilo, 1500)
    } catch (err) {
      editarHilo((prev) => prev
        ? { ...prev, messages: prev.messages.filter((m) => m.message_id !== optimista.message_id) }
        : prev)
      setDraft(texto)
      if (errorCode(err) === 'outside_window') {
        // Meta rechazó por ventana cerrada aunque el portal la creía abierta
        // (el reloj corrió entre el fetch y el envío). Se refresca el detalle
        // para que aparezca el bloque de plantilla.
        setSendError(sinPlantillas(detail)
          ? 'La ventana de 24 h se cerró y el mensaje no salió. La conversación entró por '
            + 'el socio y por ese canal no se mandan plantillas: se habilita cuando el '
            + 'cliente vuelva a escribir.'
          : 'La ventana de 24 h se cerró. Reactivá con una plantilla aprobada.')
        refrescarHilo()
      } else if (errorCode(err) === SESION_DEVUELTA) {
        // La sesión volvió a Software entre que se abrió el hilo y el envío (o
        // el portal es de antes del bloque): el mensaje NO salió. Se refresca
        // para que el bloque reemplace al compositor.
        setSendError(errorMessage(err, 'La sesión ya volvió al socio: el mensaje no se envió.'))
        refrescarHilo()
      } else {
        setSendError(errorMessage(err, 'No se pudo enviar el mensaje.'))
      }
    } finally {
      setSending(false)
    }
  }, [draft, detail, sending, pedido, editarHilo, refrescarHilo])

  const resolver = useCallback(async () => {
    if (!detail) return
    setResolving(true)
    setSendError(null)
    try {
      const res = await apiClient.post<{ resolved_at?: string }>(
        `/conversations/${detail.conversation_id}/resolve`, {})
      // Sale de las asignadas, pasa a «Resueltas» (DEF-047) —en el acto, sin
      // esperar al sondeo: quien la acaba de cerrar la tiene que poder
      // encontrar— y se selecciona la siguiente.
      const resuelta = conversations.find((c) => c.conversation_id === detail.conversation_id)
      const restantes = conversations.filter((c) => c.conversation_id !== detail.conversation_id)
      setConversations(restantes)
      // Como `soltar` (DEF-067): lo que estaba en camino es de antes de
      // resolverla, y la siguiente queda elegida YA —no al próximo render—,
      // para que ninguna respuesta lea que la resuelta «se fue».
      ultimaAplicada.current = pedidosLista.current
      aplicarAvisos(alResolver(avisos.current, detail.conversation_id))
      const siguiente = restantes[0]?.conversation_id ?? null
      seleccionada.current = siguiente
      if (resuelta) {
        setResueltas((prev) => [
          { ...resuelta, status: 'CLOSED', assigned_to: null, assigned_at: null,
            resolved_at: res.data?.resolved_at ?? new Date().toISOString() },
          ...prev.filter((c) => c.conversation_id !== resuelta.conversation_id),
        ])
      }
      setSelectedId(siguiente)
      void refreshMe()
    } catch (err) {
      setSendError(errorMessage(err, 'No se pudo marcar como resuelta.'))
    } finally {
      setResolving(false)
    }
  }, [detail, conversations, refreshMe, aplicarAvisos])

  // «Devolver al agente» (DEF-023): la otra salida de una derivación. No cierra
  // como «Marcar resuelta» —el próximo «Hola» del cliente abriría una sesión
  // nueva con el menú— ni deja al bot callado como «Devolver a la cola»: el
  // agente retoma los documentos donde quedaron. El backend nunca le libera la
  // sesión al socio por acá.
  const devolverAlAgente = useCallback(async () => {
    if (!detail || devolviendo) return
    const id = detail.conversation_id
    setDevolviendo(true)
    setSendError(null)
    try {
      await apiClient.post(`/conversations/${id}/return-to-agent`, {})
      devueltaAlAgente.current = id
      setAviso('Listo: la conversación volvió al agente IA. Sigue con los documentos desde donde quedó.')
      refrescarHilo()
      void fetchList(true)
    } catch (err) {
      // El 409 trae el motivo ya redactado por el backend (ventana cerrada, o
      // que ya no estaba en manos humanas); se refresca para que el botón y la
      // ventana se pinten con el estado real.
      setSendError(errorMessage(err, 'No se pudo devolver la conversación al agente.'))
      if (errorCode(err) === 'outside_window' || errorCode(err) === 'not_in_human_handling'
          || errorCode(err) === SESION_DEVUELTA) {
        refrescarHilo()
      }
    } finally {
      setDevolviendo(false)
    }
  }, [detail, devolviendo, refrescarHilo, fetchList])

  // Extender la sesión del socio (06-oct-2026): el asesor que atiende se la
  // queda hasta 2 h más, una vez. El backend decide si se puede y hasta cuándo
  // —nunca más allá de la ventana de WhatsApp del cliente—; acá solo se pide,
  // se avisa con la hora que devolvió y se refresca el hilo.
  const extenderSesion = useCallback(async () => {
    if (!detail || extendiendo) return
    setExtendiendo(true)
    setSendError(null)
    try {
      const res = await apiClient.post<{ sesion_socio?: SesionSocio | null }>(
        `/conversations/${detail.conversation_id}/extend-session`, {})
      const hasta = res.data?.sesion_socio?.fin?.fin_at
      setAviso(hasta
        ? `Listo: la sesión sigue con nosotros hasta las ${horaEnLima(hasta)}.`
        : 'Listo: la sesión se extendió.')
      refrescarHilo()
    } catch (err) {
      // El 409 trae el motivo redactado (ya extendida, sin ventana, devuelta);
      // se refresca para que el aviso se pinte con el estado real.
      setSendError(errorMessage(err, 'No se pudo extender la sesión.'))
      refrescarHilo()
    } finally {
      setExtendiendo(false)
    }
  }, [detail, extendiendo, refrescarHilo])

  useEffect(() => {
    if (!aviso) return
    const t = setTimeout(() => setAviso(null), 6000)
    return () => clearTimeout(t)
  }, [aviso])

  const reactivar = useCallback(async () => {
    if (!detail) return
    setReactivating(true)
    setSendError(null)
    try {
      await apiClient.post('/conversations/reply-template', {
        conversation_id: detail.conversation_id,
        template_name: 'reengagement_pgh_v1',
        parameters: [detail.contact_name?.trim() || 'estimado(a) cliente'],
      })
      setTimeout(refrescarHilo, 1500)
    } catch (err) {
      setSendError(
        errorCode(err) === 'templates_disabled'
          ? 'La plantilla "reengagement_pgh_v1" todavía no está aprobada en Meta Business '
            + 'Manager. Una vez aprobada, un admin activa WHATSAPP_TEMPLATES_ENABLED.'
          : errorMessage(err, 'No se pudo enviar la plantilla.'),
      )
    } finally {
      setReactivating(false)
    }
  }, [detail, refrescarHilo])

  const revisarDoc = useCallback(async (
    docId: string, accion: 'approve' | 'reject' | 'request', motivo = '',
  ) => {
    if (!detail) return
    setSendError(null)
    if (accion === 'request') {
      // "Pedir de nuevo" es un mensaje al cliente, no una decisión sobre el
      // documento: el `DOC#` no cambia. El requisito sí (DEF-043), pero cuando
      // el mensaje SALE (DEF-066, QA de TEST 07-oct-2026): acá no se le pide
      // nada al servidor. Se arma el chip con la tarjeta y se carga el
      // borrador para que el asesor lo ajuste; el ✕ deshace las dos cosas.
      const doc = detail.documents.find((d) => d.doc_id === docId)
      setPedido(pedidoDeNuevo(docId, doc))
      setDraft(BORRADOR_PEDIR_DE_NUEVO)
      setTab('copiloto')
      return
    }
    setReviewingDoc(docId)
    try {
      // El motivo del rechazo que eligió el ejecutivo (diseño «Turno 2»,
      // 20-oct-2026): el revisor lo guarda en `human_notes` y la Ficha lo dice.
      // La misma llamada que En vivo (`api/revision.ts`).
      await registrarRevision(docId, accion, motivo)
      refrescarHilo()
    } catch (err) {
      setSendError(errorMessage(err, 'No se pudo registrar la revisión.'))
    } finally {
      setReviewingDoc(null)
    }
  }, [detail, refrescarHilo])

  // ── Salirse: reasignar o devolver a la cola ─────────────────────────────────

  // Saca la conversación de la bandeja en el acto y abre la siguiente, con el
  // compositor limpio. No espera al próximo sondeo: quien se salió sabe que se
  // salió, y ver la fila un rato más haría pensar que no funcionó.
  const soltar = useCallback((id: string, detalle: string) => {
    const ida = conversations.find((c) => c.conversation_id === id)
    const restantes = conversations.filter((c) => c.conversation_id !== id)
    setConversations(restantes)
    // Lo pedido antes de salirse ya no vale: la traería de vuelta (DEF-067).
    ultimaAplicada.current = pedidosLista.current
    aplicarAvisos(alSoltar(avisos.current, ida, detalle, restantes))
    seleccionada.current = null
    setSelectedId(null)
    setDraft('')
    setPedido(null)
    setSendError(null)
    setReasignando(false)
  }, [conversations, aplicarAvisos])

  const abrirReasignar = useCallback(async () => {
    setReasignando(true)
    setSendError(null)
    try {
      const res = await apiClient.get<{ advisors: Advisor[] }>('/advisors')
      setAdvisors(res.data.advisors ?? [])
    } catch (err) {
      setSendError(errorMessage(err, 'No se pudo cargar la lista de asesores.'))
      setReasignando(false)
    }
  }, [])

  const reasignarA = useCallback(async (email: string) => {
    if (!detail) return
    setReasignandoBusy(true)
    try {
      await apiClient.post(`/conversations/${detail.conversation_id}/assign`,
                           { target_email: email })
      const nombre = advisors.find((a) => a.email === email)?.name || email.split('@')[0]
      soltar(detail.conversation_id, `La atiende ${nombre} desde ahora.`)
      void fetchList(true)
    } catch (err) {
      setSendError(errorMessage(err, 'No se pudo reasignar.'))
    } finally {
      setReasignandoBusy(false)
    }
  }, [detail, advisors, soltar, fetchList])

  const devolverALaCola = useCallback(async () => {
    if (!detail) return
    setReasignandoBusy(true)
    try {
      const res = await apiClient.post<{ en_cola?: boolean }>(
        `/conversations/${detail.conversation_id}/release`, {})
      soltar(detail.conversation_id, res.data.en_cola
        ? 'Volvió a la cola global: la toma el próximo asesor.'
        : 'Quedó sin asesor, y la sigue conduciendo el agente IA.')
      void fetchList(true)
    } catch (err) {
      setSendError(errorMessage(err, 'No se pudo devolver a la cola.'))
    } finally {
      setReasignandoBusy(false)
    }
  }, [detail, soltar, fetchList])

  // La sugerencia de IA no se muestra si la ventana está cerrada: ofrecer un
  // borrador que Meta va a rechazar es peor que no ofrecer nada. El backend ya
  // lo vacía, esto es el cinturón del lado del cliente. Tampoco si es de antes
  // de lo último del caso (DEF-029): QA vio el «¡Qué bueno que todo está
  // completo!» ofrecido a un cliente que acababa de pedir una persona.
  const { copilot, vigente: copilotoVigente } = copiloto
  const sugerencia = useMemo(() => {
    if (!suggestionOpen || !detail?.window.can_send_free_text || !copilotoVigente) return null
    // Con la sesión devuelta a Software el borrador tampoco le llegaría.
    if (sinCanalConElCliente(detail)) return null
    return copilot?.disponible && copilot.borrador ? copilot.borrador : null
  }, [suggestionOpen, detail, copilot, copilotoVigente])

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {nueva && (
        <BannerNuevaAsignada
          conv={nueva}
          onAtender={() => {
            seleccionar(nueva.conversation_id)
            aplicarAvisos({ ...avisos.current, nueva: null })
          }}
          onDespues={() => aplicarAvisos({ ...avisos.current, nueva: null })}
        />
      )}
      {salio && (
        <BannerSeFue salida={salio}
                     onCerrar={() => aplicarAvisos({ ...avisos.current, salio: null })} />
      )}
      {aviso && <AvisoFlotante texto={aviso} onCerrar={() => setAviso(null)} />}

      {/* El asesor es quien primero nota que algo va mal —el bot no le contestó
          al cliente, el documento no llegó— y hasta ahora tenía que avisarle a
          un supervisor para que preguntara por él. El backend recorta el
          contexto a SUS conversaciones, así que la respuesta que recibe acá
          nunca habla de casos ajenos. Arranca cerrado: esta pantalla es para
          atender. */}
      <PreguntarAlSistema colapsable />

      <div className="flex min-h-0 flex-1">
        <ConversationList
          conversations={conversations}
          resueltas={resueltas}
          selectedId={selectedId}
          onSelect={seleccionar}
          loading={listLoading}
          error={listError}
          vistosDelServidor={vistosServidor}
        />

        {/* `relative`: el cajón del panel se monta encima del chat, no de la
            lista (DEF-031, `PanelDelCaso`). */}
        <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
          {listError && conversations.length === 0 ? (
            <PanelMensaje
              titulo="No se pudo cargar la bandeja"
              detalle={listError}
              onRetry={() => void fetchList()}
            />
          ) : hilo.cargando ? (
            <PanelMensaje titulo="Cargando conversación…" />
          ) : hilo.error ? (
            <PanelMensaje
              titulo="No se pudo abrir la conversación"
              detalle={hilo.error}
              onRetry={reintentarHilo}
            />
          ) : !detail ? (
            <div className="flex flex-1 items-center justify-center p-10">
              <EmptyState
                icon="📥"
                title="No tienes conversaciones asignadas"
                description={atiende
                  ? 'Cuando entre una conversación nueva, el round-robin te la va a asignar '
                    + 'automáticamente y va a aparecer acá.'
                  : 'No estás en la rotación: acá aparecen las que tomes desde En vivo o la cola.'}
              />
            </div>
          ) : (
            <>
              <div className="flex min-h-0 min-w-[340px] flex-1 flex-col">
              {hilo.sinConexion && <AvisoSinConexion />}
              <ChatThread
                detail={detail}
                recienLlegado={recienLlegado}
                draft={draft}
                onDraftChange={setDraft}
                pedidoDeNuevo={pedido ? etiquetaDelPedido(pedido) : null}
                avisoPedido={pedido ? avisoDelPedido(detail.conversation_state) : null}
                onQuitarPedido={() => {
                  // DEF-066: el borrador precargado se va con el chip; sin él
                  // saldría como respuesta normal y tomaría la conversación.
                  setPedido(null)
                  setDraft(borradorSinElPedido)
                }}
                onSend={() => void enviar()}
                sending={sending}
                sendError={sendError}
                suggestion={sugerencia}
                onUseSuggestion={() => {
                  if (sugerencia) setDraft(sugerencia)
                  setSuggestionOpen(false)
                }}
                onDismissSuggestion={() => setSuggestionOpen(false)}
                templates={templates}
                onResolve={() => void resolver()}
                resolving={resolving}
                onReactivate={() => void reactivar()}
                reactivating={reactivating}
                onReassign={canSupervise ? () => void abrirReasignar() : undefined}
                onReturnToAgent={() => void devolverAlAgente()}
                returning={devolviendo}
                onExtendSession={() => void extenderSesion()}
                extending={extendiendo}
              />
              {reasignando && (
                <SelectorAsesor
                  advisors={advisors}
                  actual={detail.assigned_to}
                  busy={reasignandoBusy}
                  onElegir={(email) => void reasignarA(email)}
                  onCancelar={() => setReasignando(false)}
                  onCola={() => void devolverALaCola()}
                />
              )}
              </div>
              <PanelDelCaso
                detail={detail}
                copilot={copilot}
                copilotLoading={copiloto.cargando}
                copilotUpdating={copiloto.actualizando}
                onRetryCopilot={copiloto.reintentar}
                onUseAction={(texto) => { setDraft(texto); setSuggestionOpen(false) }}
                onReviewDoc={(id, accion, motivo) => void revisarDoc(id, accion, motivo)}
                reviewingDoc={reviewingDoc}
                onDocumentoSubido={refrescarHilo}
                tab={tab}
                onTabChange={setTab}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function BannerNuevaAsignada({ conv, onAtender, onDespues }: {
  conv: ConversationSummary; onAtender: () => void; onDespues: () => void
}) {
  return (
    <div className="flex shrink-0 animate-slidein items-center gap-3 border-b border-brand-border bg-brand-light px-[18px] py-2.5">
      <span className="h-[9px] w-[9px] shrink-0 animate-dot rounded-full bg-brand" />
      <p className="flex-1 text-body text-ink">
        {/* DEF-047: una resuelta que el cliente reabrió no es «nueva»: volvió.
            DEF-067: solo si la asignación vino de esa reapertura. */}
        <b>{textoDeLaNueva(conv)}</b> — {conv.contact_name || 'Sin nombre'} ·{' '}
        <span className="font-mono">{telefonoVisible(conv)}</span>
      </p>
      <button
        onClick={onAtender}
        className="shrink-0 rounded-sm2 bg-brand px-3.5 py-1.5 text-body font-bold text-white"
      >
        Atender ahora
      </button>
      <button onClick={onDespues} className="shrink-0 text-body font-bold text-brand-hover">
        Después
      </button>
    </div>
  )
}

/** La que salió de la bandeja: te la sacaron (`propia: false`, en ámbar) o te
 *  saliste tú (en verde, con a dónde fue). Ver `bandejaAvisos.Salida`. */
function BannerSeFue({ salida, onCerrar }: {
  salida: Salida<ConversationSummary>; onCerrar: () => void
}) {
  const { conv, propia } = salida
  return (
    <div className={`flex shrink-0 animate-slidein items-center gap-3 border-b px-[18px] py-2.5 ${
      propia ? 'border-brand-border bg-brand-light' : 'border-warn-border bg-warn-light'}`}>
      <span className={`h-[9px] w-[9px] shrink-0 rounded-full ${propia ? 'bg-brand' : 'bg-warn-dot'}`} />
      <p className="flex-1 text-body text-ink">
        <b>{propia ? 'Te saliste de la conversación' : 'Ya no está en tu bandeja'}</b>
        {' '}— {conv.contact_name || 'Sin nombre'} ·{' '}
        <span className="font-mono">{telefonoVisible(conv)}</span>.{' '}
        {propia ? salida.detalle : 'Se reasignó a otra persona o la tomó un supervisor.'}
      </p>
      <button onClick={onCerrar}
              className={`shrink-0 text-body font-bold ${propia ? 'text-brand-hover' : 'text-warn'}`}>
        Entendido
      </button>
    </div>
  )
}

/** Confirmación de que una acción salió bien. Abajo a la derecha, lejos del
 *  compositor, y se va sola a los pocos segundos. */
function AvisoFlotante({ texto, onCerrar }: { texto: string; onCerrar: () => void }) {
  return (
    <div
      role="status"
      className="fixed bottom-5 right-5 z-40 flex max-w-[380px] animate-slidein items-start gap-3 rounded-card border border-brand-border bg-brand-light px-4 py-3 shadow-panel"
    >
      <span className="mt-1 h-[9px] w-[9px] shrink-0 rounded-full bg-brand" />
      <p className="flex-1 text-body text-ink">{texto}</p>
      <button onClick={onCerrar} className="shrink-0 text-body font-bold text-brand-hover"
              aria-label="Cerrar aviso">
        ✕
      </button>
    </div>
  )
}

function PanelMensaje({ titulo, detalle, onRetry }: {
  titulo: string; detalle?: string; onRetry?: () => void
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
      <p className="text-page font-extrabold text-ink">{titulo}</p>
      {detalle && <p className="max-w-md text-body text-ink-muted">{detalle}</p>}
      {onRetry && <button onClick={onRetry} className="btn-ghost">Reintentar</button>}
    </div>
  )
}
